import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useI18n, useT } from '../lib/i18n'
import { usePlaceName } from '../lib/placeName'
import { useDictation } from '../lib/dictation'
import { hasSession } from '../lib/session'
import { usePlaces, type Place } from './PlaceSelect'
import { SignInSheet } from './SignInSheet'
import { Field, FormError, SaveBar, Text } from './BizFields'
import { Pill, Sheet, Spinner } from './ui'

/**
 * Ask Qafila — the mockup's `ask` sheet, step for step.
 *
 * NOT A CHAT WINDOW. The mockup is explicit about this and it is the whole
 * design: *"The assistant is not a chat window -- it is a FORM FILLER you can
 * talk to. It listens, shows what it understood as the actual booking fields,
 * asks only for what is missing, and hands over a request to confirm. Nobody
 * wants to converse with a transport app; they want to say the sentence once
 * and be done."*
 *
 * So there is no conversation and no history. There are four slots -- from,
 * to, how many, when -- and three steps:
 *
 *   prompt      speak or type, with the three examples the mockup gives
 *   listening   the microphone, pulsing, with the waveform
 *   understood  the four slots as a form, each ✓ or "needed", chips for the
 *               one still missing, and Confirm and send when none are
 *
 * The slot order is the mockup's own: from, to, passengers, when. Asking in
 * a different order than the card displays makes the card look like it moved.
 */
type Step = 'prompt' | 'listening' | 'understood'
type Slot = 'from' | 'to' | 'pax' | 'date'

interface Draft {
  heard: string
  from?: Place
  to?: Place
  pax?: number
  date?: string
}

export function AskJourney({ open, onClose }: { open: boolean; onClose: () => void }) {
  const msg = useT()
  const place = usePlaceName()
  const { locale } = useI18n()
  const nav = useNavigate()
  const places = usePlaces()
  const dictation = useDictation()

  const [step, setStep] = useState<Step>('prompt')
  const [typed, setTyped] = useState('')
  const [d, setD] = useState<Draft>({ heard: '' })
  const [err, setErr] = useState('')
  const [signIn, setSignIn] = useState(false)

  /** The first slot still empty, in the order the card lists them. */
  const need: Slot | null =
    !d.from ? 'from' : !d.to ? 'to' : !d.pax ? 'pax' : !d.date ? 'date' : null

  function reset() {
    setStep('prompt'); setTyped(''); setD({ heard: '' }); setErr('')
  }

  /*
   * The sentence goes to the same parser the rest of the app uses. It answers
   * with cities; the picker speaks in PLACES (a city, or a named point inside
   * it), so the city is matched back to its "anywhere in" entry and a named
   * point the person goes on to choose simply replaces it.
   */
  const understand = useMutation({
    mutationFn: (sentence: string) =>
      api<{
        type: string; kind: string; understood: string
        from?: { id: string; name: string }; to?: { id: string; name: string }
        seats?: number; date?: string
      }>('/ask/journey', { method: 'POST', body: { question: sentence, locale } }),
    onSuccess: (r, sentence) => {
      const cityPlace = (c?: { id: string }) =>
        c ? (places.data ?? []).find((p) => p.is_city && p.city_id === c.id) : undefined
      setD({
        heard: sentence,
        from: cityPlace(r.from),
        to: cityPlace(r.to),
        pax: r.seats,
        date: r.date ?? undefined,
      })
      setStep('understood')
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : msg('bizf.saveFailed')),
  })

  const send = useMutation({
    mutationFn: () => api<{ request: { request_no: string }; asked_count: number }>(
      '/requests/charter', {
        method: 'POST',
        body: {
          accept_terms: true as const,
          from_city_id: d.from!.city_id,
          to_city_id: d.to!.city_id,
          from_point_id: d.from!.is_city ? null : d.from!.id,
          to_point_id: d.to!.is_city ? null : d.to!.id,
          preferred_date: d.date ?? null,
          passengers: d.pax,
        },
      }),
    onSuccess: () => { onClose(); reset(); nav('/tickets') },
    onError: (e) => setErr(e instanceof ApiError ? e.message : msg('bizf.saveFailed')),
  })

  function listen() {
    setErr('')
    if (!dictation.supported) { setErr(msg('ask.voiceUnsupported')); return }
    setStep('listening')
    dictation.start(locale, (text) => {
      dictation.stop()
      understand.mutate(text)
    })
  }

  /* ------------------------------------------------------------- prompt */

  if (step === 'prompt') {
    return (
      <Sheet open={open} onClose={() => { onClose(); reset() }} title={msg('askj.title')} full>
        <div className="rounded-xl border border-accent/35 bg-accent-wash p-3.5">
          <h3 className="text-[13px] font-[650] text-accent">{msg('askj.promptTitle')}</h3>
          <p className="mt-[7px] text-[11.5px] leading-[1.65] text-ink-2">
            {msg('askj.promptBody')}
          </p>
          <ul className="mt-[9px] list-disc space-y-1 ps-4 text-[11px] leading-[1.8] text-ink-3">
            <li>{msg('askj.eg1')}</li>
            <li>{msg('askj.eg2')}</li>
            <li>{msg('askj.eg3')}</li>
          </ul>
        </div>

        <div className="mt-[18px]">
          <button type="button" onClick={listen} aria-label={msg('ask.speak')}
                  className="mx-auto grid h-[74px] w-[74px] place-items-center rounded-full
                             bg-accent text-white active:scale-95">
            <MicGlyph />
          </button>
          <p className="mt-[9px] text-center text-[11px] text-ink-3">{msg('askj.tapToSpeak')}</p>
        </div>

        <Field label={msg('askj.orType')}>
          <Text value={typed} onChange={setTyped} placeholder={msg('askj.eg1')} />
        </Field>

        <FormError>{err}</FormError>
        <SaveBar label={msg('askj.understand')} busy={understand.isPending}
                 disabled={!typed.trim()}
                 onSave={() => { setErr(''); understand.mutate(typed.trim()) }} />
      </Sheet>
    )
  }

  /* ---------------------------------------------------------- listening */

  if (step === 'listening') {
    return (
      <Sheet open={open} onClose={() => { dictation.stop(); onClose(); reset() }}
             title={msg('ask.listening')} full>
        <div className="py-2.5">
          <div className="mic-live mx-auto grid h-[74px] w-[74px] place-items-center
                          rounded-full bg-accent text-white">
            <MicGlyph />
          </div>
          {/* Nine bars, each a beat behind the last — the mockup's own
              stagger. It is the only thing on screen saying the microphone
              is actually open. */}
          <div className="mt-[13px] flex h-7 items-center justify-center gap-1">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <i key={i} className="askwave w-[3px] rounded-sm bg-accent"
                 style={{ animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>
          <p className="mt-[13px] text-center text-[11px] text-ink-3">{msg('askj.sayWhat')}</p>
        </div>
        {understand.isPending && <Spinner />}
        <FormError>{err}</FormError>
        <SaveBar label={msg('askj.stop')}
                 onSave={() => { dictation.stop(); setStep('prompt') }} />
      </Sheet>
    )
  }

  /* --------------------------------------------------------- understood */

  const chips: { value: string; label: string }[] =
    need === 'from' || need === 'to'
      ? (places.data ?? []).filter((p) => !p.is_city).slice(0, 6)
          .map((p) => ({ value: p.id, label: place(p.name) }))
      : need === 'pax'
        ? ['2', '6', '12', '22'].map((n) => ({ value: n, label: n }))
        : need === 'date'
          ? nextDays(3).concat([{ value: '', label: msg('date.anyDay') }])
          : []

  function fill(value: string) {
    if (need === 'from' || need === 'to') {
      const p = (places.data ?? []).find((x) => x.id === value)
      setD({ ...d, [need]: p })
    } else if (need === 'pax') setD({ ...d, pax: Number(value) })
    else if (need === 'date') setD({ ...d, date: value || 'any' })
  }

  const row = (label: string, value: string | undefined, slot: Slot) => (
    <div key={slot} className="flex items-center justify-between gap-3 border-b border-line
                               py-[11px] last:border-b-0">
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] uppercase tracking-[0.8px] text-ink-3">{label}</span>
        <span className={`mt-[3px] block text-[13px] ${
          value ? 'font-[650]' : 'italic text-band-amber'}`}>
          {value || msg('askj.notSaid')}
        </span>
      </span>
      {value
        ? <Pill tone="ok">✓</Pill>
        : <Pill tone="warn">{msg('askj.needed')}</Pill>}
    </div>
  )

  return (
    <Sheet open={open} onClose={() => { onClose(); reset() }}
           title={msg(need ? 'askj.almost' : 'askj.ready')} full>
      <p className="text-[13px] leading-[1.55] text-ink-3">{msg('askj.hereIsWhat')}</p>

      <p className="mt-[11px] rounded-xl border border-line bg-surface p-3 text-[12.5px]
                    italic leading-[1.6] text-ink-2">“{d.heard}”</p>

      <div className="mt-3 rounded-xl border border-line bg-surface px-[15px]">
        {row(msg('charter.from'), place(d.from?.name), 'from')}
        {row(msg('charter.to'), place(d.to?.name), 'to')}
        {row(msg('travel.passengers'), d.pax ? String(d.pax) : undefined, 'pax')}
        {row(msg('charter.when'), d.date === 'any' ? msg('date.anyDay') : d.date, 'date')}
      </div>

      {need ? (
        <>
          <h2 className="mb-3 mt-[21px] text-[15px] font-[650]">{msg(`askj.ask.${need}`)}</h2>
          <div className="flex flex-wrap gap-[7px]">
            {chips.map((c) => (
              <button key={c.value || c.label} type="button" onClick={() => fill(c.value)}
                      className="rounded-lg border border-line bg-surface px-3 py-2.5
                                 text-[12px] font-[650] active:border-accent">
                {c.label}
              </button>
            ))}
          </div>
          <p className="mt-[11px] text-[11px] text-ink-3">{msg('askj.orSayIt')}</p>
          <FormError>{err}</FormError>
          <SaveBar label={msg('ask.speak')} onSave={listen} />
        </>
      ) : (
        <>
          <div className="mt-3.5 rounded-xl border border-accent/35 bg-accent-wash p-3.5">
            <h3 className="text-[13px] font-[650] text-accent">{msg('ask.nothingBookedYet')}</h3>
            <p className="mt-[7px] text-[11.5px] leading-[1.65] text-ink-2">
              {msg('askj.confirmBody')}
            </p>
          </div>
          <FormError>{err}</FormError>
          <SaveBar label={msg('ask.confirmAndSend')} busy={send.isPending}
                   onSave={() => {
                     setErr('')
                     // The code is asked for at the moment it buys something,
                     // and this is that moment -- not when the sheet opened.
                     if (!hasSession()) { setSignIn(true); return }
                     send.mutate()
                   }} />
          <button type="button" onClick={reset}
                  className="mt-2 min-h-[44px] w-full rounded-lg text-[13px] font-[650] text-ink-3">
            {msg('ask.startAgain')}
          </button>
        </>
      )}

      <SignInSheet open={signIn} onClose={() => setSignIn(false)}
                   onDone={() => { setSignIn(false); send.mutate() }} />
    </Sheet>
  )
}

function MicGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-[30px] w-[30px]" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
      <path d="M12 18v3" />
    </svg>
  )
}

/** The next few days, as the mockup's date chips. */
function nextDays(n: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  for (let i = 1; i <= n; i++) {
    const d = new Date()
    d.setDate(d.getDate() + i)
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    out.push({ value: iso, label: d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) })
  }
  return out
}
